import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { bootOnce } from '../../bootstrap';
import { authedFetchJson, authedApiJsonOrThrow } from '../../utils/api';
import { GoldenButtonPopup, GoldenButtonOffer } from './GoldenButtonPopup';

export function GoldenButton(props: { modelId: number | string }) {
  const modelId = Number(props.modelId) || 0;
  const [enabled, setEnabled] = useState(false);
  const [offer, setOffer] = useState<GoldenButtonOffer | null>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const boot = useMemo(() => bootOnce(), []);

  // Fetch golden button offer for this model
  useEffect(() => {
    if (!modelId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await authedFetchJson<{
          enabled: boolean;
          offer: GoldenButtonOffer | null;
        }>(boot, `/api/models/${modelId}/golden-button`);
        if (cancelled) return;
        if (r.ok && r.data) {
          setEnabled(r.data.enabled === true);
          setOffer(r.data.offer || null);
        }
      } catch (e: any) {
        console.warn('[GoldenButton] fetch error', e?.message || e);
      }
      if (!cancelled) setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [boot, modelId]);

  const handleClick = useCallback(() => {
    if (!offer) return;
    setPopupOpen(true);
  }, [offer]);

  const handlePopupClose = useCallback(() => {
    setPopupOpen(false);
  }, []);

  const handleCtaClick = useCallback(async () => {
    if (!offer) return;
    // Record the click
    try {
      await authedApiJsonOrThrow(boot, `/api/golden-button/${offer.id}/click`, {
        method: 'POST',
        body: JSON.stringify({ model_id: modelId }),
      });
    } catch (e: any) {
      console.warn('[GoldenButton] click track error', e?.message || e);
    }
    // Open the URL in a new tab (Telegram will handle appropriately)
    const url = String((offer.outbound_url || offer.cta_url) || '').trim();
    if (url) {
      try {
        const tg = (window as any).Telegram?.WebApp;
        if (tg?.openLink) {
          tg.openLink(url);
        } else {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      } catch {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    }
    setPopupOpen(false);
  }, [boot, modelId, offer]);

  // Don't render if not enabled or no offer
  if (!loaded || !enabled || !offer) return null;

  return (
    <>
      <button
        class="golden-button"
        onClick={handleClick}
        aria-label="Get Free Spins"
        style={{
          position: 'absolute',
          left: '16px',
          top: 'calc(var(--chat-video-render-h, 100%) - 72px)',
          zIndex: 150,
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          border: '2px solid rgba(255, 255, 255, 0.3)',
          background: 'linear-gradient(135deg, #FFD700, #FFA500)',
          boxShadow: '0 4px 15px rgba(255, 215, 0, 0.4)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          animation: 'goldenPulse 2s ease-in-out infinite',
          transition: 'transform 100ms ease',
          pointerEvents: 'auto',
        }}
      >
        🎰
      </button>

      <GoldenButtonPopup
        open={popupOpen}
        offer={offer}
        onClose={handlePopupClose}
        onCtaClick={handleCtaClick}
      />

      <style>{`
        @keyframes goldenPulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 4px 15px rgba(255, 215, 0, 0.4);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 6px 20px rgba(255, 215, 0, 0.6);
          }
        }

        .golden-button:hover {
          transform: scale(1.1) !important;
        }

        .golden-button:active {
          transform: scale(0.95) !important;
        }
      `}</style>
    </>
  );
}
